import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { areaService } from '../services/areaService';
import { Area, CreateAreaForm, UpdateAreaForm, CambiarEstadoAreaForm, CambiarDesactivacionAreaForm } from '../types';
import { useCrudResource } from './useCrudResource';

export const useAreas = () => {
  const queryClient = useQueryClient();

  const crud = useCrudResource<Area, CreateAreaForm, UpdateAreaForm>({
    queryKey: ['areas'],
    invalidateKeys: [['areas-activas'], ['areas-estado']],
    fetchList: async () => {
      const response = await areaService.getAreas();
      if (!response.success) {
        throw new Error(response.message || 'Error al cargar las áreas');
      }
      return response.data || [];
    },
    create: async (area) => {
      const response = await areaService.createArea(area);
      if (!response.success) {
        throw new Error(response.message || 'Error al crear el área');
      }
      return response.data;
    },
    update: async (id, area) => {
      const response = await areaService.updateArea(id, area);
      if (!response.success) {
        throw new Error(response.message || 'Error al actualizar el área');
      }
      return response.data;
    },
    remove: async (id) => {
      const response = await areaService.deleteArea(id);
      if (!response.success) {
        throw new Error(response.message || 'Error al eliminar el área');
      }
    },
  });

  const {
    data: areasActivas,
    isLoading: isLoadingActivas,
    error: errorActivas,
    refetch: refetchAreasActivas,
  } = useQuery({
    queryKey: ['areas-activas'],
    queryFn: async () => {
      const response = await areaService.getAreasActivas();
      if (!response.success) {
        throw new Error(response.message || 'Error al cargar las áreas activas');
      }
      return response.data || [];
    },
    retry: 2,
    retryDelay: 1000,
  });

  const {
    data: estadoAreas,
    isLoading: isLoadingEstado,
    error: errorEstado,
    refetch: refetchEstadoAreas,
  } = useQuery({
    queryKey: ['areas-estado'],
    queryFn: async () => {
      const response = await areaService.getEstadoAreas();
      if (!response.success) {
        throw new Error(response.message || 'Error al cargar el estado de las áreas');
      }
      return response.data || [];
    },
    retry: 2,
    retryDelay: 1000,
  });

  const cambiarEstadoMutation = useMutation({
    mutationFn: async ({ id, estado }: { id: number; estado: CambiarEstadoAreaForm }) => {
      const response = await areaService.cambiarEstado(id, estado);
      if (!response.success) {
        throw new Error(response.message || 'Error al cambiar el estado del área');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['areas'] });
      queryClient.invalidateQueries({ queryKey: ['areas-activas'] });
      queryClient.invalidateQueries({ queryKey: ['areas-estado'] });
    },
    onError: () => {},
  });

  const cambiarDesactivacionMutation = useMutation({
    mutationFn: async ({ id, desactivacion }: { id: number; desactivacion: CambiarDesactivacionAreaForm }) => {
      const response = await areaService.cambiarDesactivacion(id, desactivacion);
      if (!response.success) {
        throw new Error(response.message || 'Error al cambiar la desactivación del área');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['areas'] });
      queryClient.invalidateQueries({ queryKey: ['areas-activas'] });
      queryClient.invalidateQueries({ queryKey: ['areas-estado'] });
    },
    onError: () => {},
  });

  const updateArea = (
    vars: { id: number; area: UpdateAreaForm },
    options?: Parameters<typeof crud.update>[1]
  ) => crud.update({ id: vars.id, data: vars.area }, options);

  return {
    areas: crud.items,
    areasActivas: areasActivas || [],
    estadoAreas: estadoAreas || [],
    isLoading: crud.isLoading,
    isLoadingActivas,
    isLoadingEstado,
    error: crud.error,
    errorActivas,
    errorEstado,
    createArea: crud.create,
    updateArea,
    deleteArea: crud.remove,
    cambiarEstado: cambiarEstadoMutation.mutate,
    cambiarDesactivacion: cambiarDesactivacionMutation.mutate,
    isCreating: crud.isCreating,
    isUpdating: crud.isUpdating,
    isDeleting: crud.isRemoving,
    isCambiandoEstado: cambiarEstadoMutation.isPending,
    isCambiandoDesactivacion: cambiarDesactivacionMutation.isPending,
    refetchAreas: crud.refetch,
    refetchAreasActivas,
    refetchEstadoAreas,
    createError: crud.createError,
    updateError: crud.updateError,
    deleteError: crud.removeError,
    cambiarEstadoError: cambiarEstadoMutation.error,
    cambiarDesactivacionError: cambiarDesactivacionMutation.error,
  };
};
